import { useQuery } from "@tanstack/react-query";
import type { ManualAssetFolderListing } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import { assetApi } from "../../../api/client.js";

/** Which pool the asset drawer is browsing: the realm-wide **Master** ("All manuals") or a single manual's **project** files. */
export type AssetScope = { kind: "master" } | { kind: "manual"; manualId: number };

/**
 * Asset-library browse query (ADR-024 Phase 11a-3 + addendum #217 P2). Two scopes over one hook:
 *   - **master** → `GET /assets/folders` (the realm-wide pool; `folderId` null ⇒ the Master root);
 *   - **manual** → `GET /manuals/:id/assets/folders` (that manual's OWN project folder; `folderId` null ⇒ the manual root; a
 *     manual with no folder yet resolves `folder: null` → an empty tab).
 * `types` is the pick-mode extension filter (sent comma-separated; the server marks each file `selectable`). Both responses
 * share the {@link ManualAssetFolderListing} shape (folder NULLABLE — the Master folder is always present, a manual's may not
 * be yet), so the drawer renders both identically. The scope is part of the query key, so switching tabs refetches cleanly.
 */
export function useAssetFolder(
  session: AuthSession | null,
  scope: AssetScope,
  folderId: number | null,
  types: string[] | null,
): { data: ManualAssetFolderListing | null; isPending: boolean } {
  const typesCsv = types && types.length > 0 ? types.join(",") : undefined;
  const scopeKey = scope.kind === "manual" ? `manual:${scope.manualId}` : "master";
  const q = useQuery({
    queryKey: ["asset-folder", session?.realm, session?.sub, scopeKey, folderId, typesCsv ?? ""],
    enabled: session !== null,
    queryFn: async (): Promise<ManualAssetFolderListing> => {
      if (scope.kind === "manual") {
        const res = await assetApi.getManualAssetFolder({ params: { manualId: scope.manualId }, query: { folderId: folderId ?? undefined, types: typesCsv } });
        if (res.status !== 200) throw new Error(`manual asset browse failed (${res.status})`);
        return res.body;
      }
      const res = await assetApi.getAssetFolder({ query: { folderId: folderId ?? undefined, types: typesCsv } });
      if (res.status !== 200) throw new Error(`asset browse failed (${res.status})`);
      return res.body;
    },
  });
  return { data: q.data ?? null, isPending: q.isPending };
}
